import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateCompaction } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

export default function CompactionModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:compaction:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { basis: 'volume', volume: '', area: '', depth: '', layers: [] })
  const initial = superseding ? { ...superseding.engineering_inputs } : draft

  const [basis, setBasisRaw] = useState(initial.basis || 'volume')
  const [volume, setVolumeRaw] = useState(initial.volume ?? '')
  const [area, setAreaRaw] = useState(initial.area ?? '')
  const [depth, setDepthRaw] = useState(initial.depth ?? '')
  const [layers, setLayersRaw] = useState(initial.layers || [])

  const mk = (setter, key) => (v) => { setter(v); if (!superseding) setDraft((d) => ({ ...d, [key]: v })) }
  const setBasis = mk(setBasisRaw, 'basis')
  const setVolume = mk(setVolumeRaw, 'volume')
  const setArea = mk(setAreaRaw, 'area')
  const setDepth = mk(setDepthRaw, 'depth')
  const setLayers = mk(setLayersRaw, 'layers')

  function addLayer() { setLayers([...layers, { id: `L${Date.now()}`, thickness: '', label: '' }]) }
  function updateLayer(id, patch) { setLayers(layers.map((l) => (l.id === id ? { ...l, ...patch } : l))) }
  function removeLayer(id) { setLayers(layers.filter((l) => l.id !== id)) }

  const result = useMemo(() => {
    try {
      return { value: calculateCompaction({
        basis, volume: volume === '' ? NaN : Number(volume), area: area === '' ? NaN : Number(area), depth: depth === '' ? NaN : Number(depth),
        layers: layers.map((l) => ({ thickness: Number(l.thickness), label: l.label })),
      }), error: null }
    } catch (e) { return { value: null, error: e.message } }
  }, [basis, volume, area, depth, layers])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { basis, volume: basis === 'volume' ? Number(volume) : undefined, area: basis === 'area' ? Number(area) : undefined, depth: basis === 'area' ? Number(depth) : undefined, layers }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'm3', geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: result.value, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <L label="Measurement Basis">
        <Select value={basis} onChange={(e) => setBasis(e.target.value)} style={{ width: '100%' }}><option value="volume">Volume (m³)</option><option value="area">Area × Depth</option></Select>
      </L>
      {basis === 'volume' ? (
        <L label="Volume (m³)"><Input type="number" value={volume} onChange={(e) => setVolume(e.target.value)} style={{ width: '100%' }} /></L>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <L label="Area (m²)"><Input type="number" value={area} onChange={(e) => setArea(e.target.value)} style={{ width: '100%' }} /></L>
          <L label="Depth (m)"><Input type="number" value={depth} onChange={(e) => setDepth(e.target.value)} style={{ width: '100%' }} /></L>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 6px' }}>
        <span style={{ fontSize: 11, fontWeight: 700 }}>Layers (descriptive detail only — never multiplied)</span>
        <Button size="sm" variant="secondary" onClick={addLayer}>+ Add Layer</Button>
      </div>
      {layers.map((l) => (
        <div key={l.id} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <Input placeholder="Label" value={l.label} onChange={(e) => updateLayer(l.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
          <Input type="number" placeholder="Thickness (m)" value={l.thickness} onChange={(e) => updateLayer(l.id, { thickness: e.target.value })} style={{ width: 100, fontSize: 11 }} />
          <button onClick={() => removeLayer(l.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
        </div>
      ))}
      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
          {result.value.layerCheck && <div style={{ fontSize: 11, color: result.value.layerCheck.mismatch ? colors.warningText : colors.textSecondary, marginBottom: 6 }}>Layer sum: {formatNumber(result.value.layerCheck.layerSum)} m {result.value.layerCheck.mismatch ? '⚠ does not match depth (informational only)' : ''}</div>}
          <div style={{ fontWeight: 700 }}>Final Quantity: {formatNumber(result.value.finalQuantity)} m³</div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div style={{marginBottom:10}}><label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 4 }}>{label}</label>{children}</div> }
